import { useState, useEffect, useMemo, Fragment } from "react";
import type { JSX } from "react";

import './Projects.css';

import { GetDataPage, RegisterDataPage, SetListDataSingleItem, UnRegisterDataPage } from "../../interface/Data";
import { Svg_AltOrder, Svg_Arrow, Svg_ArrowLeaning, Svg_Cheked, Svg_Close_Search, Svg_Dashboard, Svg_Delete, Svg_Edit, Svg_Eye, Svg_EyeClose, Svg_Filter, Svg_Homies, Svg_Location, Svg_Menu_Client, Svg_Platform, Svg_PointMenu, Svg_Search, Svg_Undo, Svg_YearRelease, Svg_YearSale } from "../../components/Svg";
import { Input_Text } from "../../components/Input";

import { AltStatus, CallbackError } from "../../services/Function";
import { OpenPage } from "../../services/OpenPage";
import { Reg_ProjectHandleData } from "../../services/Register";

export interface DataProject {
    id              : number;
    year_release    : string | null;
    year_sale       : string | null;
    value_sold      : string | null;
    type_register   : number;
    status          : number;
    link_project    : string | null;
    link_dashboard  : string | null;
    bd_name         : string | null;
    company         : number;
    company_name?   : string | null;
    name_project    : string;
    unit_type       : string | null;
    other_unit_type : string | null;
    latitude        : string | null;
    longitude       : string | null;
    country_name    : string | null;
    country_sigla   : string | null;
    postal_code     : string | null;
    icon_map        : string | null;
    logo            : string | null;
    bk_image        : string | null;
    publish_on_home : number;
    publish_on_map  : number;
    date_to_delete  : string | null;
    register_id     : number;
    register_date   : string;
    removed_id      : number | null;
    removed_date    : string | null;
}

// "bk_image" chega como texto JSON: [{ "file": "...", "status": false }, ...]
export interface ProjectBkImage {
    file   : string;
    status : boolean;
}

// "other_unit_type" chega como texto JSON: ["HIGH_RISE_MULTI_TOWER", ...]
export function ParseUnitTypeList(value: string | null): string[] {
    try {
        const list = JSON.parse(value || '[]');
        return Array.isArray(list) ? list : [];
    }catch {
        return [];
    }
}

export function ParseBkImage(value: string | null): ProjectBkImage[] {
    try {
        const list = JSON.parse(value || '[]');
        return Array.isArray(list) ? list : [];
    }catch {
        return [];
    }
}

const notFile = "./assets/not_file.jpg";

interface PendingUpdate {
    id    : number;
    field : string;
    value : string;
}

type FilterType = 'Clients' | 'Location' | 'Homies';

interface FilterOption {
    type  : FilterType;
    value : string;
    label : string;
}

const listFilterType: FilterType[] = ['Clients', 'Location', 'Homies'];

const listOrderBy = ['Clients', 'Location', 'Homies', 'Year of release', 'Year of sale'] as const;
type OrderBy = typeof listOrderBy[number];

// valor do projeto usado para casar com a opção marcada no filtro
function FilterValue(elem: DataProject, type: FilterType): string {
    if(type === 'Clients'){
        return String(elem.company);
    }
    if(type === 'Location'){
        return elem.country_name || elem.country_sigla || '';
    }
    return elem.unit_type || '';
}

// o mesmo ícone serve para a categoria do filtro e para a opção de ordenação
function IconOpt(props: { value: FilterType | OrderBy; className?: string }): JSX.Element {
    const className = props.className || "icons";

    switch(props.value){
        case 'Location':
            return <Svg_Location className={ className } color="#ffffff" />;
        case 'Homies':
            return <Svg_Homies className={ className } color="#ffffff" />;
        case 'Year of release':
            return <Svg_YearRelease className={ className } color="#ffffff" />;
        case 'Year of sale':
            return <Svg_YearSale className={ className } color="#ffffff" />;
        default:
            return <Svg_Menu_Client className={ className } color="#ffffff" />;
    }
}

export default function Page_Projects() {

    const [ listData, setListData ] = useState<{ [key: string]: DataProject }>(GetDataPage("projects") || {});
    const [ listClient, setListClient ] = useState<{ [key: string]: { comporate_name: string } }>(GetDataPage("clients") || {});

    const [ showSubmenu, setShowSubmenu ] = useState<number | null>(null);
    const [ pendingUpdate, setPendingUpdate ] = useState<PendingUpdate | null>(null);
    const [ search, setSearch ] = useState('');

    const [ searchOpt, setSearchOpt ] = useState('');           // menu aberto: '', 'filter' ou 'order'
    const [ filterOptSub, setFilterOptSub ] = useState('');     // categoria aberta dentro do filtro
    const [ searchList, setSearchList ] = useState<FilterOption[]>([]);      // marcados no menu
    const [ appliedFilter, setAppliedFilter ] = useState<FilterOption[]>([]); // aplicados no "Save filter"
    const [ orderBy, setOrderBy ] = useState<OrderBy | ''>('');

    function NameCompany(elem: DataProject){
        return elem.company_name || listClient[elem.company]?.comporate_name || '';
    }

    // chave da opção escolhida em "Order": ordena a lista e separa os grupos
    function OrderValue(elem: DataProject): string {
        switch(orderBy){
            case 'Clients':
                return NameCompany(elem);
            case 'Location':
                return FilterValue(elem, 'Location');
            case 'Homies':
                return FilterValue(elem, 'Homies');
            case 'Year of release':
                return elem.year_release || '';
            case 'Year of sale':
                return elem.year_sale || '';
            default:
                return '';
        }
    }

    // a mesma chave, como aparece no cabeçalho do grupo
    function GroupName(elem: DataProject): string {
        const value = OrderValue(elem);

        if(value === ''){
            return 'Not defined';
        }
        return orderBy === 'Homies' ? value.replace(/_/g, ' ') : value;
    }

    // as opções do filtro saem dos próprios projetos, então só aparece o que existe cadastrado
    const filterOptions = useMemo(() => {
        const listOpt: { [key in FilterType]: { [key: string]: string } } = { Clients: {}, Location: {}, Homies: {} };

        Object.values(listData).forEach((elem)=>{
            const company = elem.company_name || listClient[elem.company]?.comporate_name || '';
            if(company){
                listOpt.Clients[String(elem.company)] = company;
            }

            const country = elem.country_name || elem.country_sigla || '';
            if(country){
                listOpt.Location[country] = country;
            }

            if(elem.unit_type){
                listOpt.Homies[elem.unit_type] = elem.unit_type.replace(/_/g, ' ');
            }
        });

        function ShowList(type: FilterType): FilterOption[] {
            return Object.keys(listOpt[type])
                .map((value)=>({ type: type, value: value, label: listOpt[type][value] }))
                .sort((a, b)=> a.label.localeCompare(b.label));
        }

        return { Clients: ShowList('Clients'), Location: ShowList('Location'), Homies: ShowList('Homies') };
    }, [listData, listClient]);

    const dataPage = useMemo(() => {
        let list = Object.values(listData);

        if(search !== ''){
            const text = search.toLowerCase();
            list = list.filter((elem)=>{
                const company = elem.company_name || listClient[elem.company]?.comporate_name || '';
                return (elem.name_project || '').toLowerCase().includes(text) ||
                    company.toLowerCase().includes(text) ||
                    (elem.country_name || '').toLowerCase().includes(text) ||
                    (elem.country_sigla || '').toLowerCase().includes(text);
            });
        }

        if(appliedFilter.length > 0){
            // dentro da mesma categoria as opções somam, entre categorias o projeto
            // precisa atender todas
            const listType: { [key: string]: string[] } = {};
            appliedFilter.forEach((opt)=>{
                listType[opt.type] = [ ...(listType[opt.type] || []), opt.value ];
            });

            list = list.filter((elem)=>{
                return Object.keys(listType).every((type)=>{
                    return listType[type].includes(FilterValue(elem, type as FilterType));
                });
            });
        }

        if(orderBy !== ''){
            list = [...list].sort((a, b)=> OrderValue(a).localeCompare(OrderValue(b)));
        }

        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listData, listClient, search, appliedFilter, orderBy]);

    // soma de "value_sold" de cada grupo, exibida no cabeçalho
    const groupTotals = useMemo(() => {
        const totals: { [key: string]: number } = {};

        if(orderBy !== ''){
            dataPage.forEach((elem)=>{
                const value = parseFloat(String(elem.value_sold || '').replace(/[^0-9.,]/g, '').replace(',', '.')) || 0;
                const name  = GroupName(elem);
                totals[name] = (totals[name] || 0) + value;
            });
        }

        return totals;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataPage, orderBy]);

    function CheckedData(opt: FilterOption){
        return searchList.some(item => item.type === opt.type && item.value === opt.value);
    }

    function HandleData(opt: FilterOption){
        const newData = [...searchList];
        const index   = newData.findIndex(item => item.type === opt.type && item.value === opt.value);

        if(index === -1){
            newData.push(opt);
        }else {
            newData.splice(index, 1);
        }
        setSearchList(newData);
    }

    function SearchFilter(){
        setAppliedFilter(searchList);
        setSearchOpt('');
        setFilterOptSub('');
    }

    function ReseteFilter(){
        setSearchList([]);
        setAppliedFilter([]);
        setSearchOpt('');
        setFilterOptSub('');
    }

    function SearchOrderBy(value: OrderBy){
        setOrderBy(value);
        setSearchOpt('');
    }

    function OpenLink(link: string | null){
        if(link){
            window.open(link, '_blank', 'noopener,noreferrer');
        }
    }

    function SaveHandleData(id: number, field: string, value: string | number){
        Reg_ProjectHandleData(id, field, value).then((status)=>{
            if(!status){
                CallbackError("Error saving information", "There was an issue saving the project details. Please try again.");
            }
        });
    }

    // Atualiza o valor na tela na hora. Os campos de texto passam pelo debounce,
    // os demais são gravados direto.
    function HandleStatusSubMenu(elem: DataProject, field: string, value: string | number){
        SetListDataSingleItem('projects', elem.id, { ...elem, [field]: value });

        if(field === 'year_release' || field === 'year_sale' || field === 'value_sold'){
            // trocou de campo ou de projeto antes do debounce fechar: grava o
            // anterior agora, senão ele é descartado junto com o timer
            if(pendingUpdate && (pendingUpdate.id !== elem.id || pendingUpdate.field !== field)){
                SaveHandleData(pendingUpdate.id, pendingUpdate.field, pendingUpdate.value);
            }
            setPendingUpdate({ id: elem.id, field: field, value: String(value) });

        }else {
            SaveHandleData(elem.id, field, value);
        }
    }

    useEffect(() => {
        RegisterDataPage("projects", setListData);
        RegisterDataPage("clients", setListClient);
        return () => {
            UnRegisterDataPage("projects", setListData);
            UnRegisterDataPage("clients", setListClient);
        };
    }, []);

    // Debounce dos inputs de year_release, year_sale e value_sold
    useEffect(() => {
        if(pendingUpdate){
            const timer = setTimeout(() => {
                SaveHandleData(pendingUpdate.id, pendingUpdate.field, pendingUpdate.value);
            }, 1000);

            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingUpdate]);

    return (
        <div className="page_data_list Page_Projects">
            <div className="div_search">
                <div className="div_input">
                    <Svg_Search className="icons icon_search" color="#B1B2B2" />
                    <Input_Text title="Search" type="text" value={ search } setValue={ setSearch } placeholder="Search by project" required={ false } />
                </div>

                <div className="opt_search">
                    <div className={ appliedFilter.length > 0 ? "div_icons_search div_filter search_active" : "div_icons_search div_filter" }>
                        <div className={ searchOpt === "filter" ? "type_search search_selected" : "type_search" }>
                            <div className="div_name_search" onClick={ ()=>{ setSearchOpt(searchOpt === 'filter' ? '' : 'filter'); setFilterOptSub(''); } }>
                                <Svg_Filter className="icons icons_filter" color={ searchOpt === "filter" || appliedFilter.length > 0 ? "#ffffff" : "#B1B2B2" } />
                                <div>Filter</div>
                            </div>

                            {
                                appliedFilter.length > 0 ?
                                    <div className="opt_sub_selected">
                                        <div className="div_number">{ appliedFilter.length }</div>
                                        <div onClick={ ()=>{ ReseteFilter() } }>
                                            <Svg_Close_Search className="icons icon_close" color="#ffffff" />
                                        </div>
                                    </div>
                                : null
                            }
                        </div>

                        {
                            searchOpt === "filter" ?
                                <div className="div_list_opt">
                                    {
                                        listFilterType.map((type, index)=>{
                                            const classLast = index === listFilterType.length - 1 ? " list_opt_remuve_border" : "";

                                            return(
                                                // Fragment e não uma div: o submenu precisa ser irmão direto de
                                                // "list_opt" dentro de "div_list_opt", senão a largura e os
                                                // cantos arredondados do CSS deixam de bater
                                                <Fragment key={ type }>
                                                    <div className={ filterOptSub === type ? "list_opt opt_selected" + classLast : "list_opt" + classLast }>
                                                        <div className="div_name_search" onClick={ ()=>{ setFilterOptSub(filterOptSub === type ? '' : type) } }>
                                                            <div className="icon_name">
                                                                <IconOpt value={ type } />
                                                                <div>{ type }</div>
                                                            </div>
                                                            <Svg_Arrow className="icons icons_arrow" color="#ffffff" />
                                                        </div>
                                                    </div>

                                                    {
                                                        filterOptSub === type ?
                                                            <div className="sub_opt_search">
                                                                <div className="div_btn_inf">
                                                                    <div className="list_opt_click">
                                                                        {
                                                                            filterOptions[type].length > 0 ?
                                                                                filterOptions[type].map((elem)=>{
                                                                                    return(
                                                                                        <div className="list_opt_search" key={ elem.value } onClick={ ()=>{ HandleData(elem) } }>
                                                                                            <div className="checkbox">
                                                                                                {
                                                                                                    CheckedData(elem) ?
                                                                                                        <Svg_Cheked className="icons icon_checkbox" color="#EDA13F" />
                                                                                                    : null
                                                                                                }
                                                                                            </div>
                                                                                            <div className="icon_name">{ elem.label }</div>
                                                                                        </div>
                                                                                    )
                                                                                })
                                                                            :
                                                                            <div className="no_opt_search">No option registered</div>
                                                                        }
                                                                    </div>

                                                                    <div className="btn_search" onClick={ ()=>{ SearchFilter() } }>
                                                                        Save filter
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        : null
                                                    }
                                                </Fragment>
                                            )
                                        })
                                    }
                                </div>
                            : null
                        }
                    </div>

                    <div className={ orderBy !== '' ? "div_icons_search search_active" : "div_icons_search" }>
                        <div className={ searchOpt === "order" ? "type_search search_selected" : "type_search" }>
                            <div className="div_name_search" onClick={ ()=>{ setSearchOpt(searchOpt === 'order' ? '' : 'order'); setFilterOptSub(''); } }>
                                <Svg_AltOrder className="icons icons_order" color={ searchOpt === "order" || orderBy !== '' ? "#ffffff" : "#B1B2B2" } />
                                <div>Order</div>
                            </div>

                            {
                                orderBy !== '' ?
                                    <div className="opt_sub_selected">
                                        <IconOpt value={ orderBy } className="icons icon_search_selected" />
                                        <div>{ orderBy }</div>
                                        <div onClick={ ()=>{ setOrderBy(''); setSearchOpt(''); } }>
                                            <Svg_Close_Search className="icons icon_close" color="#ffffff" />
                                        </div>
                                    </div>
                                : null
                            }
                        </div>

                        {
                            searchOpt === "order" ?
                                <div className="div_list_opt">
                                    {
                                        listOrderBy.map((elem, index)=>{
                                            const classLast = index === listOrderBy.length - 1 ? " list_opt_remuve_border" : "";

                                            return(
                                                <div className={ orderBy === elem ? "list_opt opt_selected" + classLast : "list_opt" + classLast } key={ elem } onClick={ ()=>{ SearchOrderBy(elem) } }>
                                                    <div className="icon_name">
                                                        <IconOpt value={ elem } />
                                                        <div>{ elem }</div>
                                                    </div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                            : null
                        }
                    </div>

                    {
                        searchOpt !== '' ?
                            <div className="all_sub_menu" onClick={ ()=>{ setSearchOpt(''); setFilterOptSub(''); } } />
                        : null
                    }
                </div>
            </div>

            <table cellPadding={ 0 } cellSpacing={ 0 }>
                <thead>
                    <tr>
                        <th rowSpan={ 2 } align="center" style={ { width: "20px" } }>#</th>
                        <th rowSpan={ 2 }>Project</th>
                        <th rowSpan={ 2 }>Company</th>
                        <th rowSpan={ 2 }>Country</th>
                        <th colSpan={ 3 } align="center">Year</th>
                        <th rowSpan={ 2 } align="right" style={ { width: "70px" } }>#</th>
                    </tr>
                    <tr>
                        <th align="center" style={ { width: "60px", borderTopLeftRadius: "0", borderLeft: "0" } }>Release</th>
                        <th align="center" style={ { width: "60px" } }>Sale</th>
                        <th align="center" style={ { width: "100px", borderTopRightRadius: "0" } }>Sold</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        dataPage.length > 0 ?
                            dataPage.map((elem, index) => {
                                const classStatus   = elem.status === 0 ? 'data_disabled' : elem.status === 2 ? 'data_delete' : '';
                                const classStatusTD = elem.status === 0 ? 'disabled_td' : '';

                                const titleDisable = elem.status === 1 ? "Disable project" : "Enable project";
                                const textDisable  = elem.status === 1
                                    ? `Are you sure you want to disable the project "${ elem.name_project }"?`
                                    : `Are you sure you want to enable the project "${ elem.name_project }"?`;

                                // Nas 3 últimas linhas o menu abre para cima, para não estourar o fim da
                                // tabela. Só vale quando existem pelo menos 4 registros.
                                const openMenuTop = dataPage.length >= 4 && index >= dataPage.length - 3;

                                const titleDelete = elem.status === 2 ? "Reactivate project" : "Delete project";
                                const textDelete  = elem.status === 2
                                    ? `Are you sure you want to cancel the removal of the project "${ elem.name_project }"?`
                                    : `Are you sure you want to delete the project "${ elem.name_project }"?`;

                                // com a ordenação ativa a lista sai agrupada: o cabeçalho entra
                                // sempre que a chave muda em relação à linha anterior
                                const groupName = orderBy !== '' ? GroupName(elem) : '';
                                const showGroup = orderBy !== '' && (index === 0 || groupName !== GroupName(dataPage[index - 1]));

                                return (
                                    <Fragment key={ elem.id }>
                                    {
                                        showGroup ?
                                            <tr className="group_order_header">
                                                <td colSpan={ 8 }>
                                                    { groupName } - Total value: { groupTotals[groupName].toLocaleString('en-US', { style: 'currency', currency: 'USD' }) }
                                                </td>
                                            </tr>
                                        : null
                                    }

                                    <tr className={ classStatus }>
                                        <td align="center" className={ classStatusTD }>{ index + 1 }</td>
                                        <td className={ classStatusTD }>
                                            <div className="div_img_td">
                                                <div className="img_td" style={ { backgroundImage: `url("${ elem.logo || notFile }")` } } />
                                                <div>{ elem.name_project }</div>
                                            </div>
                                        </td>
                                        <td className={ classStatusTD }>{ NameCompany(elem) }</td>
                                        <td className={ classStatusTD }>{ elem.country_name || elem.country_sigla || '-' }</td>

                                        <td className={ classStatusTD } align="center">
                                            <input type="text" className="input_td" maxLength={ 4 } value={ elem.year_release || '' } onChange={ (e)=>{ HandleStatusSubMenu(elem, 'year_release', e.target.value) } } placeholder="0000" />
                                        </td>
                                        <td className={ classStatusTD } align="center">
                                            <input type="text" className="input_td" maxLength={ 4 } value={ elem.year_sale || '' } onChange={ (e)=>{ HandleStatusSubMenu(elem, 'year_sale', e.target.value) } } placeholder="0000" />
                                        </td>
                                        <td className={ classStatusTD } align="center">
                                            <input type="text" className="input_td" value={ elem.value_sold || '' } onChange={ (e)=>{ HandleStatusSubMenu(elem, 'value_sold', e.target.value) } } placeholder="0" />
                                        </td>

                                        <td className={ classStatusTD } align="right">
                                            <div className="opt_click">
                                                <div className="list_opt list_opt_end">
                                                    {
                                                        // marcado para remoção: no lugar do olho entra o restaurar, que é
                                                        // a única ação que importa nesse estado
                                                        elem.status === 2 ?
                                                            <div data-tooltip-id="show_alert" data-tooltip-content="Restore project" data-tooltip-place="top" onClick={ ()=>{ setShowSubmenu(null); AltStatus('projects', titleDelete, 'reactivate', elem.id, textDelete, "1"); } }>
                                                                <Svg_Undo className="icons" color="#B1B2B2" />
                                                            </div>
                                                        :
                                                        <div  data-tooltip-id="show_alert" data-tooltip-content={ elem.status === 1 ? "Click to hide" : "Click to show" } data-tooltip-place="top" onClick={ ()=>{ setShowSubmenu(null); AltStatus('projects', titleDisable, 'disable_enable', elem.id, textDisable, elem.status === 1 ? "0" : "1"); } }>
                                                            {
                                                                elem.status === 1 ?
                                                                    <Svg_Eye className="icons" color="#B1B2B2" />
                                                                :
                                                                <Svg_EyeClose className="icons" color="#B1B2B2" />
                                                            }
                                                        </div>
                                                    }

                                                    <div  data-tooltip-id="show_alert" data-tooltip-content="Edit project" data-tooltip-place="top" onClick={ ()=>{ setShowSubmenu(null); OpenPage('projects_details', elem.id); } }>
                                                        <Svg_Edit className="icons" color="#B1B2B2" />
                                                    </div>

                                                    <div data-tooltip-id="show_alert" data-tooltip-content="More options" data-tooltip-place="top" onClick={ ()=>{ setShowSubmenu(showSubmenu === elem.id ? null : elem.id) } }>
                                                        <Svg_PointMenu className="icons icon_point" color="#B1B2B2" />
                                                    </div>
                                                </div>

                                                {
                                                    showSubmenu === elem.id ?
                                                        <div className={ openMenuTop ? "div_sub_menu div_sub_menu_top" : "div_sub_menu" }>
                                                            <div className="opt_sub_menu" onClick={ ()=>{ HandleStatusSubMenu(elem, 'publish_on_home', elem.publish_on_home ? 0 : 1) } }>
                                                                <div className="checkbox_sub_menu">
                                                                    {
                                                                        elem.publish_on_home ?
                                                                            <Svg_Cheked className="icons icon_checkbox" color="#EDA13F" />
                                                                        : null
                                                                    }
                                                                </div>
                                                                <div className="name_opt_sub_menu">Publish on Bees home page</div>
                                                            </div>

                                                            <div className="opt_sub_menu" onClick={ ()=>{ HandleStatusSubMenu(elem, 'publish_on_map', elem.publish_on_map ? 0 : 1) } }>
                                                                <div className="checkbox_sub_menu">
                                                                    {
                                                                        elem.publish_on_map ?
                                                                            <Svg_Cheked className="icons icon_checkbox" color="#EDA13F" />
                                                                        : null
                                                                    }
                                                                </div>
                                                                <div className="name_opt_sub_menu">Publish on Bees map</div>
                                                            </div>

                                                            <div className={ elem.link_project ? "opt_sub_menu" : "opt_sub_menu opt_sub_menu_off" } onClick={ ()=>{ setShowSubmenu(null); OpenLink(elem.link_project); } }>
                                                                <Svg_Platform className="icons" color="#B1B2B2" />
                                                                <div className="name_opt_sub_menu">Open platform</div>
                                                            </div>

                                                            <div className={ elem.link_dashboard ? "opt_sub_menu" : "opt_sub_menu opt_sub_menu_off" } onClick={ ()=>{ setShowSubmenu(null); OpenLink(elem.link_dashboard); } }>
                                                                <Svg_Dashboard className="icons" color="#B1B2B2" />
                                                                <div className="name_opt_sub_menu">Open dashboard</div>
                                                            </div>

                                                            <div className={ elem.latitude && elem.longitude ? "opt_sub_menu" : "opt_sub_menu opt_sub_menu_off" } onClick={ ()=>{ setShowSubmenu(null); OpenLink(elem.latitude && elem.longitude ? `https://www.google.com/maps/search/?api=1&query=${ elem.latitude },${ elem.longitude }` : null); } }>
                                                                <Svg_Location className="icons" color="#B1B2B2" />
                                                                <div className="name_opt_sub_menu">Show location</div>
                                                            </div>

                                                            <div className="opt_sub_menu" onClick={ ()=>{ setShowSubmenu(null); AltStatus('projects', titleDelete, elem.status === 2 ? 'reactivate' : 'delete', elem.id, textDelete, "2"); } }>
                                                                <Svg_Delete className="icons" color="#B1B2B2" />
                                                                <div className="name_opt_sub_menu">{ titleDelete }</div>
                                                            </div>
                                                        </div>
                                                    : null
                                                }
                                            </div>

                                            {
                                                elem.status === 2 ?
                                                    <div className="div_status_delete" data-tooltip-id="show_alert" data-tooltip-content={ `It will be deleted on ${ elem.date_to_delete }` } data-tooltip-place="top">
                                                        { elem.date_to_delete }
                                                    </div>
                                                : null
                                            }
                                        </td>
                                    </tr>
                                    </Fragment>
                                );
                            })
                        :
                        <tr>
                            <td colSpan={ 8 } align="center">No data found</td>
                        </tr>
                    }
                </tbody>
            </table>

            {
                showSubmenu !== null ?
                    <div className="all_sub_menu" onClick={ ()=>{ setShowSubmenu(null) } } />
                : null
            }
        </div>
    );
}
